"use client";

import { CitationTool } from "@/components/citation-tool";
import { useToolUi } from "@/components/tools/tool-ui";
import { getFeaturedStyles } from "@/data/citation-styles";

const EXAMPLES = [
  {
    style: "APA 7th edition",
    label: "Reference list (presentation)",
    example:
      "Author, A. A., & Author, B. B. (Year, Month Day–Day). Paper title [Paper presentation]. Conference Name, Location. https://doi.org/xxxx",
    inText: "(Author & Author, Year)",
  },
  {
    style: "MLA 9th edition",
    label: "Works Cited",
    example:
      '"Paper Title." Conference Name, Day Month Year, Location. Edited by First Last, Publisher, Year, pp. 123–45.',
    inText: "(Author)",
  },
  {
    style: "IEEE",
    label: "Reference list (proceedings)",
    example:
      'A. Author and B. Author, "Paper title," in Proc. Conference Name, Location, Year, pp. 123–145.',
    inText: "[1]",
  },
];

/** English strings. A translated content file overrides these through `ui`. */
export const CONFERENCE_PAPER_CITATION_GENERATOR_STRINGS = {
  conferencePaperCitationExamples: "Conference Paper Citation Examples",
  inText: "In-text: ",
  presentationVsProceedings: "Presentation vs. Proceedings",
  howYouCiteAConference:
    "How you cite a conference paper depends on whether it was",
  delivered: "delivered",
  or: " or",
  published: "published",
  unpublishedPresentationPosterOrSession:
    "Unpublished presentation, poster, or session.",
  inApaAddABracketed: "In APA, add a bracketed descriptor after the title —",
  paperPresentation: "[Paper presentation]",
  posterSession: "[Poster session]",
  or2: ", or",
  conferenceSession: "[Conference session]",
  thenTheConferenceNameAnd:
    " — then the conference name and location. Use the date range of the event as the date.",
  paperPublishedInProceedings: "Paper published in proceedings.",
  treatItLikeAChapter:
    "Treat it like a chapter in an edited book (or a journal article for IEEE): give the paper title, the proceedings/conference name, the editors or volume, the page range, the publisher, and a DOI. Fill in the Pages and Conference Name fields above.",
  howToCiteAConference: "How to Cite a Conference Paper",
  n1GatherThePaperDetails: "1. Gather the paper details.",
  noteTheAuthorSThe:
    "Note the author(s), the paper title, the conference name, the location, the year (or full event dates), the page range if it was published, and any DOI or URL.",
  n2EnterThemPickA: "2. Enter them & pick a style.",
  theGeneratorAboveOpensOn: "The generator above opens on the",
  conferencePaper: "Conference Paper",
  sourceTypeFillTheFields:
    "source type — fill the fields in and choose APA, MLA, Chicago, IEEE, or any of the {styleCount} styles.",
  n3Cite: "3. Cite.",
  weBuildAFullReference:
    " We build a full reference with the authors, title, conference, and date. Edit any field, then copy or export it to BibTeX, RIS, CSV, or Word.",
} as const;

export default function ConferencePaperCitationGeneratorWidget() {
  const strings = useToolUi(CONFERENCE_PAPER_CITATION_GENERATOR_STRINGS);

  return (
    <>
      {/* Live generator — opens on the Conference Paper (CSL paper-conference) manual-entry form */}
      <CitationTool
        initialManualType="paper-conference"
        initialMode="manual"
        styles={getFeaturedStyles()}
      />
      {/* Examples */}
      <section className="space-y-4">
        <h2 className="font-semibold text-foreground text-xl">
          {strings.conferencePaperCitationExamples}
        </h2>
        <div className="space-y-4">
          {EXAMPLES.map((ex) => (
            <div
              className="rounded-lg border border-border bg-muted/30 p-4"
              key={ex.style}
            >
              <p className="mb-1 font-semibold text-foreground text-sm">
                {ex.style}
                <span className="ml-2 font-normal text-muted-foreground">
                  {ex.label}
                </span>
              </p>
              <p className="font-mono text-foreground text-sm leading-relaxed">
                {ex.example}
              </p>
              <p className="mt-2 text-muted-foreground text-sm">
                {strings.inText}
                <span className="font-mono">{ex.inText}</span>
              </p>
            </div>
          ))}
        </div>
      </section>
      {/* Presentation vs proceedings explainer */}
      <section className="space-y-4">
        <h2 className="font-semibold text-foreground text-xl">
          {strings.presentationVsProceedings}
        </h2>
        <p className="text-muted-foreground text-sm leading-relaxed">
          {strings.howYouCiteAConference}{" "}
          <strong className="text-foreground">{strings.delivered}</strong>
          {strings.or}{" "}
          <strong className="text-foreground">{strings.published}</strong>:
        </p>
        <ul className="space-y-3 text-muted-foreground text-sm leading-relaxed">
          <li>
            <strong className="text-foreground">
              {strings.unpublishedPresentationPosterOrSession}
            </strong>{" "}
            {strings.inApaAddABracketed}{" "}
            <span className="font-mono">{strings.paperPresentation}</span>,{" "}
            <span className="font-mono">{strings.posterSession}</span>
            {strings.or2}{" "}
            <span className="font-mono">{strings.conferenceSession}</span>
            {strings.thenTheConferenceNameAnd}
          </li>
          <li>
            <strong className="text-foreground">
              {strings.paperPublishedInProceedings}
            </strong>{" "}
            {strings.treatItLikeAChapter}
          </li>
        </ul>
      </section>
      {/* How to */}
      <section className="space-y-4">
        <h2 className="font-semibold text-foreground text-xl">
          {strings.howToCiteAConference}
        </h2>
        <ol className="space-y-3 text-muted-foreground text-sm leading-relaxed">
          <li>
            <strong className="text-foreground">
              {strings.n1GatherThePaperDetails}
            </strong>{" "}
            {strings.noteTheAuthorSThe}
          </li>
          <li>
            <strong className="text-foreground">
              {strings.n2EnterThemPickA}
            </strong>{" "}
            {strings.theGeneratorAboveOpensOn}{" "}
            <span className="font-medium text-foreground">
              {strings.conferencePaper}
            </span>{" "}
            {strings.sourceTypeFillTheFields}
          </li>
          <li>
            <strong className="text-foreground">{strings.n3Cite}</strong>
            {strings.weBuildAFullReference}
          </li>
        </ol>
      </section>
    </>
  );
}

/**
 * English only for now. Add locales here as this page type is translated — the
 * registry in src/lib/i18n/localized-routes.ts has to agree, or the proxy will
 * redirect the URL this generates away again.
 */
export function generateStaticParams() {
  return [{ locale: "en" }];
}

export const dynamicParams = false;
